import { Capacitor } from '@capacitor/core';

import { isStoreBuild } from './platform';

/**
 * Адреса политики и условий — ОДНИМ МЕСТОМ (25.09.2026, сборка 61).
 *
 * Наши страницы `/privacy` и `/terms` — тот же текст, что в Google Docs (`docs/refs/app-legal-2026-09-22/`).
 * Места, которые ОТКРЫВАЮТ документ (пункт «Документы» в настройках, подпись под формой входа, подвал сайта),
 * берут адрес через `privacyUrl()` / `termsUrl()`; текст самих документов ссылается на наши страницы
 * (`absoluteLegalUrl(PRIVACY_URL)`) — они открываются и вне приложения.
 *
 * ⚠️ Адреса нигде больше не пишутся строкой — сторож `legalLinks.test.ts`. Две редакции одного
 * документа (одна по старой ссылке, другая по новой) — именно то, что ревьюер сверяет с анкетой.
 */
export const PRIVACY_URL = '/privacy';
export const TERMS_URL = '/terms';
export const DELETE_ACCOUNT_URL = '/delete-account';

/**
 * Документы в Google Docs — их адреса стоят в карточке App Store kz.onebilim.academy (решение владельца 26.09.2026).
 * iOS-приложение открывает именно их: ревьюер сверяет документ в приложении с документом по ссылке из карточки.
 */
export const PRIVACY_GDOC = 'https://docs.google.com/document/d/1Hxi6TiO0u9KNuUgpKSFtgayjjaT4BpNjOuI5EIoPUaQ/';
export const TERMS_GDOC = 'https://docs.google.com/document/d/10ZQQI2a9QA6XmGxKF24xdl9RoROniqfRwlRUCEBbjAM/';

export type LegalPlatform = 'ios' | 'android' | 'web';

/**
 * Какой адрес открывать на площадке (26.09.2026): iOS-приложение — Google Docs, Android-приложение и сайт —
 * свои `/privacy`, `/terms` (переход внутри приложения, с кнопкой «назад»). Чистая функция — для проверки.
 */
export function legalUrlFor(doc: 'privacy' | 'terms', platform: LegalPlatform): string {
  if (platform === 'ios') return doc === 'privacy' ? PRIVACY_GDOC : TERMS_GDOC;
  return doc === 'privacy' ? PRIVACY_URL : TERMS_URL;
}

function currentPlatform(): LegalPlatform {
  if (!isStoreBuild()) return 'web';
  return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
}

/** Политика — для места, которое её открывает. */
export const privacyUrl = (): string => legalUrlFor('privacy', currentPlatform());
/** Условия — для места, которое их открывает. */
export const termsUrl = (): string => legalUrlFor('terms', currentPlatform());

/** Откуда открываются наши страницы, если ссылку нужно дать полным адресом (внутри текста документа). */
const SITE = 'https://academy.onebilim.kz';

export function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/** Полный адрес — для текста документа, который читают и вне приложения. */
export function absoluteLegalUrl(url: string): string {
  return isExternal(url) ? url : `${SITE}${url}`;
}

/**
 * Открыть документ.
 *
 * Наш адрес открывается переходом внутри приложения: у страницы документа своя кнопка
 * «назад» (`legalBackInApp.test.ts`). Внешний адрес в приложении открывается системным окном
 * (`@capacitor/browser`): перейти на Google Docs самим WebView значило бы увести человека из
 * приложения без дороги обратно. На сайте внешний адрес открывается новой вкладкой.
 *
 * ⚠️ СБОЙ НЕ МОЛЧИТ (02.10.2026). Системное окно открывает плагин, который подгружается отдельным куском: кусок
 * не загрузился, плагина нет в сборке, окно отказало — и нажатие на «Политику» не делало НИЧЕГО, ошибку никто не
 * ловил (владелец: «где-то открываются, где-то нет»). Теперь при любом сбое открывается НАША страница того же
 * документа (`fallback`, `/privacy` или `/terms`) — внутри приложения, с кнопкой «Назад». Документ человек видит
 * всегда. `fallback` не передан — последняя попытка: обычное открытие адреса.
 */
export async function openLegal(url: string, navigate: (to: string) => void, fallback?: string): Promise<void> {
  if (!isExternal(url)) {
    navigate(url);
    return;
  }
  if (isStoreBuild()) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
    } catch {
      if (fallback) navigate(fallback);
      else window.open(url, '_blank', 'noopener');
    }
    return;
  }
  window.open(url, '_blank', 'noopener');
}

/** Наша страница того же документа — запасной путь для `openLegal`. */
export const privacyFallback = (): string => PRIVACY_URL;
export const termsFallback = (): string => TERMS_URL;
